import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const SIDE_IMG =
  'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=900&q=80';
const PANEL_IMG =
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=900&q=80';

const Benefits = () => {
  const { t } = useLanguage();

  return (
    <section id="reasons" className="py-24 bg-sand/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <span className="text-coral text-sm font-bold uppercase tracking-[0.18em]">{t.reasons.kicker}</span>
          <h2 className="mt-4 text-4xl lg:text-5xl font-extrabold text-ink font-heading">
            {t.reasons.title}{' '}
            <span className="text-teal">{t.reasons.titleAccent}</span>
          </h2>
          <p className="mt-4 text-lg text-muted">{t.reasons.intro}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          <div
            className="lg:col-span-7 relative overflow-hidden min-h-[400px] ring-1 ring-line group"
            style={{ borderRadius: '1.75rem' }}
          >
            <img
              src={SIDE_IMG}
              alt={t.reasons.imageAlt}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              width={900}
              height={1100}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/20 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 lg:p-10">
              <p className="font-accent italic text-2xl lg:text-3xl text-white leading-snug max-w-md">
                &ldquo;{t.reasons.quote}&rdquo;
              </p>
              <p className="mt-4 text-white/80 max-w-md text-sm leading-relaxed">{t.reasons.quoteSub}</p>
              <a
                href="/s"
                className="mt-8 inline-flex items-center gap-2 w-fit bg-coral hover:bg-coral-light text-white font-bold px-6 py-3 transition-all hover:-translate-y-0.5 shadow-soft"
                style={{ borderRadius: '12px' }}
              >
                {t.reasons.cta}
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-6">
            <div
              className="bg-surface border border-line p-8 flex-1"
              style={{ borderRadius: '1.25rem' }}
            >
              <ul className="space-y-5">
                {t.reasons.bullets.map((line) => (
                  <li key={line} className="flex gap-4 items-start">
                    <span
                      className="mt-0.5 w-8 h-8 bg-teal/10 flex items-center justify-center shrink-0"
                      style={{ borderRadius: '10px' }}
                    >
                      <Check className="w-4 h-4 text-teal" />
                    </span>
                    <span className="text-ink font-medium leading-snug">{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4 flex-1">
              <div
                className="relative overflow-hidden min-h-[160px] ring-1 ring-line col-span-2 sm:col-span-1"
                style={{ borderRadius: '1rem' }}
              >
                <img
                  src={PANEL_IMG}
                  alt={t.reasons.panelAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                  width={450}
                  height={300}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-dark/90 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-coral-light">{t.reasons.panelKicker}</p>
                  <p className="text-white font-heading font-bold text-base mt-0.5">{t.reasons.panelTitle}</p>
                </div>
              </div>
              <div
                className="bg-teal text-white p-5 flex flex-col justify-center col-span-2 sm:col-span-1"
                style={{ borderRadius: '1rem' }}
              >
                <p className="text-white/90 text-sm leading-relaxed">{t.reasons.panelBody}</p>
                <p className="mt-3 text-white/70 text-xs leading-relaxed border-t border-white/20 pt-3">
                  {t.reasons.panelSub}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;
